/* eslint-disable @typescript-eslint/no-explicit-any */
import { EventSystem } from '@pbk-web/state';
import { systemDefaultLanguageCode } from '@pbk-web/types';
import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import axios from 'axios';
import i18next from 'i18next';
import qs from 'qs';

import { PbkHeaders } from './PbkHeaders';

const baseURL = import.meta.env['VITE_API_BASE_URL'] as string;
const shouldDeDupeRequestsLocally = !!import.meta.hot;

const logger = import.meta.env['VITE_DEBUG_REQUESTS'] === 'true' ? console.debug : () => ({});

// Sorts object keys recursively (arrays keep their order, since order is semantically significant
// there) so two payloads with the same content but different property order still produce the same
// signature below.
function isPlainObject(value: object): boolean {
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function sortKeysDeep(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortKeysDeep);
  // Only recurse into plain objects. Non-plain objects (Date, Map, class instances, …) are left as-is
  // so JSON serialization keeps their value — Object.keys() on e.g. a Date is empty and would drop it.
  if (value !== null && typeof value === 'object' && isPlainObject(value)) {
    return Object.keys(value as Record<string, unknown>)
      .sort()
      .reduce(
        (acc, key) => {
          acc[key] = sortKeysDeep((value as Record<string, unknown>)[key]);
          return acc;
        },
        {} as Record<string, unknown>,
      );
  }
  return value;
}

/**
 * Shared HTTP transport for the REST clients. Owns the single axios instance
 * (`httpClient`) that the app's error interceptor + Sentry capture attach to,
 * plus auth/tenant state, request dedup and language headers.
 *
 * Both {@link Api} (V1 `{ meta, data }` envelope) and `ApiV2` (bare direct-call
 * envelope) extend this — so every request, V1 or V2, rides the same interceptor
 * pipeline. It exposes no `get`/`post`: the response contract is the subclass's
 * concern, which keeps the two envelopes from leaking into each other.
 */
export default class ApiBase {
  private static savedToken: string | null = null;
  private static currentClientId: string | null = null;
  private static currentTenantId: string | null = null;
  private static pendingRequests: Record<any, [AbortController, any]> = {};
  public static httpClient: AxiosInstance = ApiBase.recreateHttpClient();

  protected static makeRequest(requestMethod: string, config: AxiosRequestConfig | undefined, url: string, payload?: any) {
    config ??= {};

    // dev env
    let signiture: any;
    // Only the request that registered the pending entry may clear it, and a duplicate resolves
    // with the promise it was a duplicate *of* — captured here, at the abort, because the entry
    // is gone the moment that request settles (see the cleanup below).
    let ownsPendingRequest = false;
    let duplicateOf: Promise<any> | undefined;
    if (shouldDeDupeRequestsLocally) {
      signiture = ApiBase.makeSigniture(requestMethod, config.baseURL ?? baseURL, url, { ...(payload || {}), ...(config.params || {}) });

      if (!config?.signal) {
        const controller = new AbortController();
        config = { ...config, signal: controller.signal };

        if (ApiBase.pendingRequests[signiture]) {
          logger('Canceled 2nd request:', signiture.join('_'));
          duplicateOf = ApiBase.pendingRequests[signiture][1];
          controller.abort();
        } else {
          logger('Starting request:', signiture.join('_'));
          ApiBase.pendingRequests[signiture] = [controller, null];
          ownsPendingRequest = true;
        }
      }
    }

    config.url = url;
    config.method = requestMethod;
    // Default to sending the session cookie, but let a caller opt out per request
    // (e.g. a direct-call V2 service whose CORS policy lacks AllowCredentials).
    config.withCredentials = config.withCredentials ?? true;
    config.headers = {
      ...(config.headers || {}),
      'Accept-Language': i18next.language || systemDefaultLanguageCode,
      [PbkHeaders.UserLanguage]: i18next.language || systemDefaultLanguageCode,
    };
    if (payload) {
      config.data = payload;
    }

    const response = ApiBase.httpClient.request(config).catch((e) => {
      if (shouldDeDupeRequestsLocally) {
        if (ownsPendingRequest) {
          delete ApiBase.pendingRequests[signiture];
        }
        if (axios.isCancel(e)) {
          // try to resolve the duplicate request with the existing request's value
          return duplicateOf ?? new Promise<any>(() => ({}));
        }
        logger('Ended request (err):', signiture.join('_'));
      }

      throw e;
    });

    if (shouldDeDupeRequestsLocally && ownsPendingRequest) {
      ApiBase.pendingRequests[signiture][1] ??= response;

      // Cleared the moment the response settles, rather than a beat later. An entry that outlives
      // its request makes the *next* identical request a "duplicate" of a finished one and answers
      // it with that response — so any read fired shortly after an identical read came back
      // pre-write data. A write's own invalidation lands well inside such a window: the builder's
      // `GET /sections` after a section create returned the list from before the create, and the
      // new section only appeared on the next reload. Collapsing genuinely concurrent duplicates
      // (the point of this) does not need the entry to outlive the request it stands for.
      // `void` plus a no-op rejection handler: this is an observer, not a second consumer. Without
      // one, `.then` forks a rejected promise nobody awaits and a caller that handles only
      // `response` still trips an unhandled-rejection report. The `catch` above already clears the
      // entry on the failure path, so there is nothing to do here.
      void response.then(
        () => {
          logger('Ended request (okay):', signiture.join('_'));
          delete ApiBase.pendingRequests[signiture];
        },
        () => undefined,
      );
    }

    return response;
  }

  private static makeSigniture(method: string, baseUrl: string, url: string, payload: any): any[] {
    const caller =
      new Error().stack
        ?.split('\n')
        .find((x) => x.includes('at') && !x.includes('Service'))
        ?.trim()
        .split(' ')[1] || '';

    // JSON.stringify the whole payload (with keys sorted so property order doesn't matter) instead of
    // walking it by hand — the previous approach fell back to bare Array.prototype.toString() for
    // nested objects, which collapses ANY object to the literal string "[object Object]" and loses
    // its actual content. That made two requests with the same shape but different nested values
    // (e.g. two different Address Book criteria rules fired concurrently) produce an identical
    // signature, so the second was wrongly treated as a duplicate and aborted in favor of the first.
    // Presence check (not a truthy check) so a falsy-but-valid body — `false`, `0`, `''` — still
    // serializes distinctly instead of collapsing to the no-body signature and colliding with an
    // empty request.
    const hasPayload = payload !== undefined && payload !== null;
    const serializedPayload = hasPayload ? JSON.stringify(sortKeysDeep(payload)) : '';

    // Include the effective host so requests to different base URLs (e.g. a V1
    // monolith call and a direct-call V2 request sharing this pool) never collide
    // on an identical relative URL + method + payload.
    return [caller, method, baseUrl, url, serializedPayload];
  }

  // Used for example when token changes
  public static recreateHttpClient(token: string | null = null): AxiosInstance {
    // Both arguments won't always be passed in, so save them for future calls
    ApiBase.savedToken = token || ApiBase.savedToken;

    ApiBase.httpClient = axios.create({
      baseURL,
      withCredentials: !ApiBase.savedToken,
      headers: {
        Authorization: ApiBase.savedToken ? `Bearer ${ApiBase.savedToken}` : undefined,
        [PbkHeaders.ClientId]: ApiBase.currentClientId || undefined,
        [PbkHeaders.TenantId]: ApiBase.currentTenantId || undefined,
      },
      validateStatus: () => true, // never throw errors if not 200 response - we do that manually
      paramsSerializer: (params) => {
        // Used because the backend expects arrays in query strings to be in format:
        // key=value1&key=value2&key=value3
        // While the default for axios is
        // key[]=value1&key[]=value2&key[]=value3
        return qs.stringify(params, { indices: false, arrayFormat: 'repeat', allowDots: true });
      },
    });

    EventSystem.fireEvent('http-client-recreated', ApiBase.httpClient);
    return ApiBase.httpClient;
  }

  public static setClientTenantDetails(clientId: string, tenantId: string) {
    ApiBase.currentClientId = clientId;
    ApiBase.currentTenantId = tenantId;
    ApiBase.recreateHttpClient();
  }

  public static unsetClientTenantDetails() {
    ApiBase.currentClientId = null;
    ApiBase.currentTenantId = null;
  }

  public static clearToken(): void {
    ApiBase.savedToken = null;
    ApiBase.recreateHttpClient(null);
  }
}
